/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { useDirection } from '@veneer/core';
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import FallbackImage from '../../assets/images/device-details-page-fallback.png';
import { breakpoints } from '../../base';
import { generateID } from '../../base/helper';
import './styles.scss';
import { DeviceDetailsPageProps } from './types';
import { useImageScaling } from './useImageScaling';

const TextWrap = styled.div.withConfig({
  shouldForwardProp: (prop: any) => prop !== 'isRTL'
})<{ isRTL: boolean }>`
  position: absolute;
  ${(props) => (props.isRTL ? 'right' : 'left')} : 80px;
  ${(props) => (!props.isRTL ? 'right' : 'left')} : 0px;
  min-width: 320px;
  box-sizing: border-box;
  z-index: 2;

  @media (max-width: ${breakpoints.md}) {
    ${(props) => (props.isRTL ? 'right' : 'left')} : 16px !important;
    ${(props) => (!props.isRTL ? 'right' : 'left')} : 16px !important;
  }
`;

const Image = styled.img<{
  $imageWidthLarge: number;
  $offsetImageTopLarge: number;
  $imageScale: number;
  $isRTL: boolean;
  $imageOffset: number;
  $imageWidthSmall: number;
  $offsetImageTopSmall: number;
  $offsetImageRightSmall: number;
  $loaded?: boolean;
}>`
  position: fixed;
  width: ${({ $imageWidthLarge }) => $imageWidthLarge}px;
  top: ${({ $offsetImageTopLarge }) => $offsetImageTopLarge}px;
  transform-origin: ${({ $isRTL }) => ($isRTL ? 'left' : 'right')} center;
  transform: scale(${({ $imageScale }) => $imageScale});
  ${({ $isRTL, $imageOffset }) => ($isRTL ? 'left' : 'right')}: ${({
    $imageOffset
  }) => $imageOffset}px;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  z-index: 1;

  @media (max-width: ${breakpoints.md}) {
    width: ${({ $imageWidthSmall }) => $imageWidthSmall}px;
    top: ${({ $offsetImageTopSmall }) => $offsetImageTopSmall}px;
    transform: none;
    ${({ $isRTL, $offsetImageRightSmall }) => ($isRTL ? 'left' : 'right')}: ${({
      $offsetImageRightSmall
    }) => $offsetImageRightSmall}px;
  }
`;

const Logo = styled.div`
  background: url(${(props: { logoImg: string }) => props.logoImg}) no-repeat;

  @media (max-width: ${breakpoints.md}) {
    background-size: contain;
  }
`;

const DeviceDetailsPage: React.FC<DeviceDetailsPageProps> = ({
  id = generateID('clientos-ui-toolkit-device-details-page'),
  deviceImageLarge,
  deviceImageSmall,
  logoImg,
  deviceName,
  deviceNickname,
  statusIndicator,
  children,
  statusList,
  imageWidthLarge,
  offsetTextWrapLarge,
  offsetImageTopLarge,
  offsetImageRightLarge,
  imageWidthSmall,
  offsetImageTopSmall,
  offsetImageRightSmall
}) => {
  const [isLargeFallback, setIsLargeFallback] = useState(false);
  const [isSmallFallback, setIsSmallFallback] = useState(false);
  const smallImageRef = useRef<HTMLImageElement>(null);
  const largeImageRef = useRef<HTMLImageElement>(null);
  const textWrapRef = useRef<HTMLHeadingElement>(null);
  const deviceInfoRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const isRTL = useDirection().isRTL;

  useEffect(() => {
    setIsLargeFallback(false);
  }, [deviceImageLarge]);

  useEffect(() => {
    setIsSmallFallback(false);
  }, [deviceImageSmall]);

  const { imageScale, imageOffset, imageLoaded } = useImageScaling({
    largeImageRef,
    smallImageRef,
    textWrapRef,
    titleRef,
    imageWidthLarge,
    offsetImageRightLarge,
    offsetTextWrapLarge,
    deviceImageLarge
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      if (smallImageRef.current) {
        smallImageRef.current.style.opacity =
          smallImageRef.current && scrollTop > 0 ? '0.2' : '1';
      }
      if (largeImageRef.current) {
        largeImageRef.current.style.opacity =
          largeImageRef.current && scrollTop > 0 ? '0.2' : '1';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [smallImageRef, largeImageRef]);

  useLayoutEffect(() => {
    if (textWrapRef.current && deviceInfoRef.current) {
      let rafId: number;

      // Create ResizeObserver to watch for width changes
      const resizeObserver = new ResizeObserver((entries) => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          for (const entry of entries) {
            const height = textWrapRef.current?.offsetHeight;
            if (height && deviceInfoRef.current) {
              deviceInfoRef.current.style.height = `${height}px`;
            }
          }
        });
      });

      // Start observing width changes
      resizeObserver.observe(textWrapRef.current);

      // Initial height set
      const height = textWrapRef.current.offsetHeight;
      if (height && deviceInfoRef.current) {
        deviceInfoRef.current.style.height = `${height}px`;
      }

      // Cleanup
      return () => {
        resizeObserver.disconnect();
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }
  }, [deviceNickname, deviceName, statusIndicator, statusList]);

  return (
    <section
      className={`clientos-ui-toolkit-device-details-page__main-section`}
      id={id}
    >
      <Image
        ref={largeImageRef}
        src={deviceImageLarge}
        className={`clientos-ui-toolkit-device-details-page__device-image ${isRTL ? 'rtl' : ''} ${isLargeFallback ? 'clientos-ui-toolkit-device-details-page__device-image--fallback' : ''}`}
        id={`${id}__large-image`}
        $loaded={imageLoaded && !isLargeFallback}
        $imageWidthLarge={imageWidthLarge}
        $offsetImageTopLarge={offsetImageTopLarge}
        $imageScale={imageScale}
        $isRTL={isRTL}
        $imageOffset={imageOffset}
        $imageWidthSmall={imageWidthSmall}
        $offsetImageTopSmall={offsetImageTopSmall}
        $offsetImageRightSmall={offsetImageRightSmall}
        onError={(e) => {
          e.currentTarget.src = FallbackImage;
          setIsLargeFallback(true);
        }}
      />
      <Image
        ref={smallImageRef}
        src={deviceImageSmall}
        className={`clientos-ui-toolkit-device-details-page__device-image clientos-ui-toolkit-device-details-page__device-image--small ${isRTL ? 'rtl' : ''} ${isSmallFallback ? 'clientos-ui-toolkit-device-details-page__device-image--fallback' : ''}`}
        id={`${id}__small-image`}
        $loaded={imageLoaded && !isSmallFallback}
        $imageWidthLarge={imageWidthLarge}
        $offsetImageTopLarge={offsetImageTopLarge}
        $imageScale={1} // No scaling for small image
        $isRTL={isRTL}
        $imageOffset={offsetImageRightLarge}
        $imageWidthSmall={imageWidthSmall}
        $offsetImageTopSmall={offsetImageTopSmall}
        $offsetImageRightSmall={offsetImageRightSmall}
        onError={(e) => {
          e.currentTarget.src = FallbackImage;
          setIsSmallFallback(true);
        }}
      />
      {logoImg && (
        <Logo
          logoImg={logoImg}
          className="clientos-ui-toolkit-device-details-page__device-logo-img"
          id={`${id}__logo-img`}
        />
      )}
      <div
        className="clientos-ui-toolkit-device-details-page__device-info"
        ref={deviceInfoRef}
        id={`${id}__device-info`}
      >
        <TextWrap
          ref={textWrapRef}
          id={`${id}__text-wrap`}
          isRTL={isRTL}
        >
          <h1
            ref={titleRef}
            className="clientos-ui-toolkit-device-details-page__device-name title-medium"
            id={`${id}__device-name`}
          >
            {deviceNickname}
          </h1>
          <h2
            className="clientos-ui-toolkit-device-details-page__device-nickname subtitle-large"
            id={`${id}__device-nickname`}
          >
            {deviceName}
          </h2>
          {(statusIndicator || statusList) && (
            <div
              className="clientos-ui-toolkit-device-details-page__device-status-region"
              id={`${id}__device-status-region`}
            >
              {statusIndicator && (
                <div
                  className="clientos-ui-toolkit-device-details-page__device-status-region__ink-widget"
                  id={`${id}__device-status`}
                >
                  {statusIndicator}
                </div>
              )}
              {statusList && (
                <div
                  className="clientos-ui-toolkit-device-details-page__device-status-region__status-list"
                  id={`${id}__device-status-list`}
                >
                  {statusList}
                </div>
              )}
            </div>
          )}
        </TextWrap>
      </div>
      <div
        className="clientos-ui-toolkit-device-details-page__center"
        id={`${id}__center`}
      >
        <div
          className={`clientos-ui-toolkit-device-details-page__body `}
          id={`${id}__body`}
        >
          {children}
        </div>
      </div>
    </section>
  );
};

export type { DeviceDetailsPageProps };
export default memo(DeviceDetailsPage);
